import { useState, useEffect, type CSSProperties } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { cn } from "../lib/utils"

const TOAST_TIMEOUT = 5000

const TOAST_POSITIONS = [
  "top-left",
  "top-center",
  "top-right",
  "bottom-left",
  "bottom-center",
  "bottom-right",
] as const

type ToastPosition = (typeof TOAST_POSITIONS)[number]

const POSITION_STYLES: Record<ToastPosition, CSSProperties> = {
  "top-left": { position: "fixed", top: 16, left: 16, right: "auto", bottom: "auto" },
  "top-center": { position: "fixed", top: 16, left: "50%", right: "auto", bottom: "auto", transform: "translateX(-50%)" },
  "top-right": { position: "fixed", top: 16, left: "auto", right: 16, bottom: "auto" },
  "bottom-left": { position: "fixed", top: "auto", left: 16, right: "auto", bottom: 16 },
  "bottom-center": { position: "fixed", top: "auto", left: "50%", right: "auto", bottom: 16, transform: "translateX(-50%)" },
  "bottom-right": { position: "fixed", top: "auto", left: "auto", right: 16, bottom: 16 },
}

export function ToastRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  const [visible, setVisible] = useState(true)
  // Theme-v2 atom defaults feed the fallback chain.
  // Note: toast.variant is a semantic severity carrier (default/success/
  // error/warning — see variantClasses below); that legacy role coexists
  // with the data-variant contract since those tokens are also valid
  // theme-variant names.
  const atomDefaults = useAtomDefaults("toast")
  const resolvedVariantRaw = element.variant ?? atomDefaults.defaultVariant ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  const rawMessage = (element as unknown as { message?: unknown }).message ?? element.text
  const message = rawMessage != null ? String(resolveSymbol(String(rawMessage)) ?? "") : ""
  const variant = resolvedVariantRaw
  const rawPosition = (element as unknown as { position?: unknown }).position
  const position: ToastPosition = typeof rawPosition === "string"
    && TOAST_POSITIONS.includes(rawPosition as ToastPosition)
    ? rawPosition as ToastPosition
    : "bottom-right"

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false)
      dispatchEvent(element.id, "dismiss", { reason: "timeout" })
    }, TOAST_TIMEOUT)
    return () => clearTimeout(timer)
  }, [element.id, dispatchEvent])

  const handleDismiss = () => {
    setVisible(false)
    dispatchEvent(element.id, "dismiss", { reason: "manual" })
  }

  if (!visible) return null

  const variantClasses: Record<string, string> = {
    default: "border bg-background text-foreground",
    success: "border-green-500/50 bg-green-50 text-green-900 dark:bg-green-950 dark:text-green-100",
    destructive: "border-destructive/50 bg-red-50 text-red-900 dark:bg-red-950 dark:text-red-100",
    error: "border-destructive/50 bg-red-50 text-red-900 dark:bg-red-950 dark:text-red-100",
    warning: "border-yellow-500/50 bg-yellow-50 text-yellow-900 dark:bg-yellow-950 dark:text-yellow-100",
  }

  return (
    <div
      role="status"
      className={cn(
        "pointer-events-auto flex w-full items-center justify-between gap-2 rounded-lg border p-4 shadow-lg transition-all",
        variantClasses[variant] ?? variantClasses.default,
        element.className,
      )}
      data-testid={`toast-${element.id}`}
      data-atom-type="toast"
      data-variant={resolvedVariantRaw}
      data-size={resolvedSize}
      data-position={position}
      style={{
        ...(element.style as CSSProperties | undefined),
        ...POSITION_STYLES[position],
      }}
    >
      <span className="text-sm font-semibold">{message}</span>
      <button
        type="button"
        className="cn-toast-dismiss inline-flex shrink-0 items-center justify-center rounded-md p-1 opacity-70 transition-opacity hover:opacity-100 focus:outline-none"
        onClick={handleDismiss}
        aria-label="Dismiss"
      >
        <span aria-hidden="true" className="text-lg leading-none">&times;</span>
      </button>
    </div>
  )
}
